import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Sparkles, MapPin, Heart, Users, Award, Phone } from "lucide-react";
import { COMPANY_INFO } from "../data";
import Sidebar from "../components/Sidebar";

export default function About() {
  return (
    <div className="pt-[72px] sm:pt-[128px]">
      {/* Hero Section */}
      <section className="relative bg-slate-950 py-24 sm:py-32 overflow-hidden text-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/66f352d5-c4b9-4e8b-8d46-eb7bb9ac1d49/ebf1a478-7590-4d02-92a6-2e357d63c109.jpg"
            alt="Om Skræp Vinduespuds"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/75 to-slate-950" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-sky-500/10 text-sky-400 border border-sky-500/20 mb-6 uppercase tracking-wider">
            Hvem er jeg?
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight uppercase">
            Om Skræp Vinduespuds
          </h1>
          <p className="text-lg text-slate-300 mt-4 max-w-2xl mx-auto leading-relaxed">
            Din lokale, fynske vinduespudser. Jeg bygger min forretning på tillid, tårnhøj kvalitet og personlig service.
          </p>
        </div>
      </section>

      {/* Main Content Layout with Sidebar */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Content (lg:col-span-8) */}
            <div className="lg:col-span-8 space-y-24">
              
              {/* Presentation Section */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                {/* Text Column */}
                <div className="md:col-span-12 space-y-6">
                  <span className="text-sky-500 font-bold text-xs uppercase tracking-wider">
                    Min Historie
                  </span>
                  <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight uppercase">
                    Professionelt håndværk med fynsk hjerte
                  </h2>
                  <div className="space-y-6 text-slate-600 text-base sm:text-lg leading-relaxed">
                    <p>
                      Skræp Vinduespuds blev grundlagt med en klar vision: At levere en vinduespudsning og ejendomsservice på Fyn, hvor kvaliteten er i top, og hvor en aftale altid er en aftale. Jeg ved, at mine kunder inviterer mig helt tæt på deres private hjem og virksomheder, og derfor vægter jeg gensidig tillid, punktlighed og diskretion utrolig højt.
                    </p>
                    <p>
                      Jeg er stolt af at være en lokal vinduespudser, der dækker hele Fyn. Mit lokalkendskab gør, at jeg hurtigt kan rykke ud, og jeg kender de specifikke udfordringer, som vind og vejr kan give ruderne i de fynske kystbyer såvel som i Odenses tætte bymiljøer.
                    </p>
                    <p>
                      Uanset om du er privat husejer, der ønsker krystalklare ruder til weekenden, eller en større erhvervsvirksomhed, der har brug for en fast abonnementsaftale til jeres glasfacader, står jeg klar med det rette udstyr, miljøvenlige produkter og et smil på læben.
                    </p>
                  </div>
                </div>

                {/* Image Column */}
                <div className="md:col-span-12 relative mt-6">
                  <div className="absolute -inset-3 bg-slate-100 rounded-md rotate-1 scale-95 opacity-70 z-0" />
                  <img
                    src="https://assets.auxodev.dk/assets/66f352d5-c4b9-4e8b-8d46-eb7bb9ac1d49/0cf23f6e-19fb-4e7f-9a92-823688f27365.jpg"
                    alt="Skræp Vinduespuds kvalitet"
                    className="relative z-10 rounded-md shadow-lg w-full object-cover h-[350px]"
                  />
                </div>
              </div>

              {/* Core Values Section */}
              <div className="space-y-10 border-t border-slate-100 pt-16">
                <div>
                  <span className="text-sky-500 font-bold text-xs uppercase tracking-wider">
                    Mit Fundament
                  </span>
                  <h2 className="text-3xl font-extrabold text-slate-900 mt-2 tracking-tight uppercase">
                    De fire søjler i mit arbejde
                  </h2>
                  <p className="text-slate-600 mt-4 text-base">
                    Jeg arbejder hver dag ud fra fire kerneværdier for at sikre dig den absolut bedste oplevelse.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Value 1: Service */}
                  <div className="bg-slate-50 p-6 rounded-md border border-slate-100 shadow-sm flex flex-col sm:flex-row gap-4">
                    <div className="bg-slate-900 text-white p-3 rounded-md h-fit shrink-0 shadow-sm">
                      <Heart className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-base mb-2 uppercase tracking-wide">Uovertruffen Service</h3>
                      <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                        Jeg møder altid mine kunder med et smil. Jeg lytter til dine specifikke ønsker og tilpasser mit arbejde, så det passer perfekt ind i din hverdag.
                      </p>
                    </div>
                  </div>

                  {/* Value 2: Kvalitet */}
                  <div className="bg-slate-50 p-6 rounded-md border border-slate-100 shadow-sm flex flex-col sm:flex-row gap-4">
                    <div className="bg-slate-900 text-white p-3 rounded-md h-fit shrink-0 shadow-sm">
                      <Award className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-base mb-2 uppercase tracking-wide">Kompromisløs Kvalitet</h3>
                      <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                        Jeg går aldrig på kompromis. Jeg anvender udelukkende professionelt udstyr, ultra-rent vand og skånsomme, miljøvenlige produkter.
                      </p>
                    </div>
                  </div>

                  {/* Value 3: Lokalkendskab */}
                  <div className="bg-slate-50 p-6 rounded-md border border-slate-100 shadow-sm flex flex-col sm:flex-row gap-4">
                    <div className="bg-slate-900 text-white p-3 rounded-md h-fit shrink-0 shadow-sm">
                      <Users className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-base mb-2 uppercase tracking-wide">Dybt Lokalkendskab</h3>
                      <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                        Som en ægte fynsk vinduespudser er jeg stolt af mine rødder. Jeg kender de lokale områder, veje og nabolag ud og ind.
                      </p>
                    </div>
                  </div>

                  {/* Value 4: Dækning af hele Fyn */}
                  <div className="bg-slate-50 p-6 rounded-md border border-slate-100 shadow-sm flex flex-col sm:flex-row gap-4">
                    <div className="bg-slate-900 text-white p-3 rounded-md h-fit shrink-0 shadow-sm">
                      <MapPin className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-base mb-2 uppercase tracking-wide">Dækning af Hele Fyn</h3>
                      <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                        Uanset om du bor i Odense, Svendborg, Nyborg eller Middelfart, kommer jeg til dig. Jeg opkræver ikke urimelige kørselstillæg.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Right Column: Sidebar (lg:col-span-4) */}
            <div className="lg:col-span-4">
              <Sidebar />
            </div>

          </div>

          {/* Geographical Coverage Section (Full Width / col-12) */}
          <div className="mt-24 border-t border-slate-100 pt-16">
            <div className="bg-slate-950 rounded-md overflow-hidden shadow-xl relative">
              <div className="absolute inset-0 opacity-15">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.4),transparent_70%)]" />
              </div>

              <div className="relative z-10 p-6 sm:p-10 lg:p-12 space-y-6">
                <div>
                  <span className="text-sky-400 font-bold text-xs uppercase tracking-wider">
                    Geografisk Dækning
                  </span>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-white mt-2 mb-4 tracking-tight uppercase">
                    Jeg pudser og renser på hele Fyn
                  </h2>
                  <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                    Min bil kører dagligt ud fra min base og dækker alle kroge af Fyn. Jeg servicerer både private parcelhuse, sommerhuse, butikker og store erhvervsejendomme. Ingen opgave er for lille, og ingen er for stor.
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 text-xs text-slate-300 font-bold uppercase tracking-wider pt-6 border-t border-white/10">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                    <span>Odense</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                    <span>Svendborg</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                    <span>Middelfart</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                    <span>Nyborg</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                    <span>Faaborg</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                    <span>Assens</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
